import { ClassType, EntityType } from '@frontend/common';
import { StatusBadge } from '@frontend/components/common';
import { ConfirmationModal } from '@frontend/components/modal';
import { DeviceActuatorClient, DeviceClient } from '@frontend/device/api';
import { Device, DeviceType } from '@frontend/device/types';
import { ErrorHandler } from '@frontend/error-handler';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { ToastUtil } from '@frontend/toast-utils';
import { FormattedMessage } from 'react-intl';

import { CardList } from '../../../analytics/card-list/card-list.component';
import OverviewHeader from '../../header/overview-header';
import { useGates } from './gates.controller';

export const Gates = () => {
    const viewProps = useGates();
    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='inline-flex gap-2'>
                        <span>{getApplicationDisplayName(Application.HARDWARE)}</span>
                        <span>/</span>
                        <span>{CommonMessage.LICENSES.HARDWARE.GATES}</span>
                    </div>
                }
            />
            <CardList<Device>
                fetch={(arg) => DeviceClient.fetchDevices({ ...arg, type: DeviceType.DOOR, sort_field: 'name', sort_direction: 'asc' })}
                dispatch={viewProps.dispatch}
                type={EntityType.DEVICE}
                useStaticListId
                eventListener={EntityType.DEVICE}
                id='hardware-gates-card-list'
                cardOptions={() => ({
                    overrideOptionsButton: (obj) => (
                        <StatusBadge
                            value={obj.status ? obj.status?.charAt(0).toUpperCase() + obj.status?.slice(1) : 'N/A'}
                            colorMap={
                                new Map([
                                    [['Open'], ClassType.SUCCESS],
                                    [['N/A'], ClassType.WARNING],
                                    [['Closed'], ClassType.SECONDARY]
                                ])
                            }
                        />
                    ),
                    actionButtons: (obj) => [
                        {
                            text: (
                                <FormattedMessage
                                    id='gate.close'
                                    defaultMessage='Close gate'
                                />
                            ),
                            className:
                                'hover:text-brand-secondary hover:outline-brand-secondary cursor-pointer p-3 bg-background-surface rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-primary inline-flex justify-center items-center gap-2 text-brand-primary text-sm font-medium',
                            hide: obj.status === 'closed' || !obj.status,
                            onClick: () =>
                                viewProps.openModal(viewProps.closeGateModalId, () => (
                                    <ConfirmationModal
                                        message={
                                            <FormattedMessage
                                                id='gate.close.confirmation'
                                                defaultMessage='Are you sure you want to close {name}?'
                                                values={{ name: obj.name }}
                                            />
                                        }
                                        onConfirm={() =>
                                            DeviceActuatorClient.openDevice(obj.account_id, obj.iot_id, obj.id)
                                                .then(() => {
                                                    ToastUtil.success(closedMessage(obj.name));
                                                    viewProps.closeModal(viewProps.closeGateModalId);
                                                })
                                                .catch((err) => ErrorHandler.handleError({ error: err }))
                                        }
                                        handleClose={() => viewProps.closeModal(viewProps.closeGateModalId)}
                                        show={true}
                                    />
                                ))
                        },
                        {
                            text: (
                                <FormattedMessage
                                    id='gate.open'
                                    defaultMessage='Open gate'
                                />
                            ),
                            className:
                                'hover:text-brand-secondary hover:outline-brand-secondary cursor-pointer p-3 bg-background-surface rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-primary inline-flex justify-center items-center gap-2 text-brand-primary text-sm font-medium',
                            hide: obj.status === 'open',
                            onClick: () =>
                                viewProps.openModal(viewProps.openGateModalId, () => (
                                    <ConfirmationModal
                                        message={
                                            <FormattedMessage
                                                id='gate.open.confirmation'
                                                defaultMessage='Are you sure you want to open {name}?'
                                                values={{ name: obj.name }}
                                            />
                                        }
                                        onConfirm={() =>
                                            DeviceActuatorClient.openDevice(obj.account_id, obj.iot_id, obj.id)
                                                .then(() => {
                                                    ToastUtil.success(openedMessage(obj.name));
                                                    viewProps.closeModal(viewProps.openGateModalId);
                                                })
                                                .catch((err) => ErrorHandler.handleError({ error: err }))
                                        }
                                        handleClose={() => viewProps.closeModal(viewProps.openGateModalId)}
                                        show={true}
                                    />
                                ))
                        }
                    ]
                })}
            />
        </div>
    );
};

const openedMessage = (name: string) => (
    <FormattedMessage
        id='gate.opened'
        values={{ name }}
        defaultMessage='{name} opened successfully'
    />
);
const closedMessage = (name: string) => (
    <FormattedMessage
        id='gate.closed'
        values={{ name }}
        defaultMessage='{name} closed successfully'
    />
);
